import React, { useMemo } from 'react';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Button, LinearProgress, Stack, Tab, Tabs } from '@mui/material';

import {
  type OfferBuilderProperty,
  formatPropertyLocation,
} from '~/components/Offers/Detail/OfferBuilder/Content/PropertyInformation';

import { salesforceHost } from '~/services/common';

import getErrorMessage from '~/utils/getErrorMessage';

import TabPanelShell from '../../../shared/TabPanelShell';
import CampaignTypeChip from '../../shared/CampaignTypeChip';

import CustomLinksPanel from './CustomLinksPanel';
import DetailsFieldsPanel from './DetailsFieldsPanel';
import { type BuildDetailsFieldsParams, type DetailFieldBySection } from './fieldHelpers';
import { buildContractualModificationsFields } from './sections/contractualModificationsFields';
import { buildCustomLinksFields } from './sections/customLinksFields';
import { buildFunctionalDetailsFields } from './sections/functionalDetailsFields';
import { buildKeyCommercialsFields } from './sections/keyCommercialsFields';
import { buildKeyContactsFields } from './sections/keyContactsFields';
import { buildKeyDatesFields } from './sections/keyDatesFields';
import { buildLocationFields } from './sections/locationFields';
import { buildOpportunityInfoFields } from './sections/opportunityInfoFields';
import { buildPartnershipGoalsFields } from './sections/partnershipGoalsFields';
import { buildPaymentDetailsFields } from './sections/paymentDetailsFields';
import { buildPropertyInfoFields } from './sections/propertyInfoFields';
import { buildSystemInfoFields } from './sections/systemInfoFields';
import { DETAIL_SECTIONS, type DetailSectionId, getDetailSection } from './types';

interface Props {
  activeSectionId: DetailSectionId;
  dealCount: number;
  error?: unknown;
  isLoading: boolean;
  onSectionChange: (sectionId: DetailSectionId) => void;
  offer?: App.Offer;
  opportunity?: App.OpportunityPage;
  opportunityId?: string;
  property?: OfferBuilderProperty;
}

function getSalesforceOpportunityUrl(opportunityId?: string) {
  if (!opportunityId || !salesforceHost) {
    return undefined;
  }

  return `${salesforceHost}/${opportunityId}`;
}

function getHeaderSubtitle(property?: OfferBuilderProperty, opportunityId?: string) {
  return [property?.name, formatPropertyLocation(property), opportunityId].filter(Boolean).join(' · ');
}

function buildDetailsFields(params: BuildDetailsFieldsParams): DetailFieldBySection {
  return {
    'opportunity-info': buildOpportunityInfoFields(params),
    'partnership-goals': buildPartnershipGoalsFields(params),
    location: buildLocationFields(params),
    'key-dates': buildKeyDatesFields(params),
    'key-commercials': buildKeyCommercialsFields(params),
    'contractual-modifications': buildContractualModificationsFields(params),
    'payment-details': buildPaymentDetailsFields(params),
    'functional-details': buildFunctionalDetailsFields(params),
    'property-info': buildPropertyInfoFields(params),
    'key-contacts': buildKeyContactsFields(params),
    'system-info': buildSystemInfoFields(params),
    'custom-links': buildCustomLinksFields(params),
  };
}

export default function DetailsTab({
  activeSectionId,
  dealCount,
  error,
  isLoading,
  offer,
  onSectionChange,
  opportunity,
  opportunityId,
  property,
}: Props) {
  const activeSection = getDetailSection(activeSectionId);
  const fieldsBySection = useMemo(
    () => buildDetailsFields({ dealCount, offer, opportunity, opportunityId, property }),
    [dealCount, offer, opportunity, opportunityId, property],
  );
  const fields = fieldsBySection[activeSectionId];
  const salesforceOpportunityUrl = getSalesforceOpportunityUrl(opportunityId);
  const campaignType = opportunity?.opportunity_info?.campaign_type__c;
  const headerSubtitle = getHeaderSubtitle(property, opportunityId);

  return (
    <TabPanelShell
      title="Opportunity Details"
      subtitle={headerSubtitle}
      actions={
        <>
          {campaignType && <CampaignTypeChip campaignType={campaignType} />}
          <Button
            disabled={!salesforceOpportunityUrl}
            href={salesforceOpportunityUrl}
            size="small"
            startIcon={<OpenInNewIcon fontSize="small" />}
            sx={{ textTransform: 'none' }}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
          >
            Salesforce
          </Button>
        </>
      }
    >
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(error, 'Unable to load Salesforce opportunity details.')}
        </Alert>
      )}

      {!isLoading && !error && !opportunity && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No Salesforce opportunity details are available for this offer yet.
        </Alert>
      )}

      <Alert icon={<LockOutlinedIcon fontSize="small" />} severity="info" sx={{ mb: 2 }}>
        Opportunity details are read-only in this view. Use Salesforce to make changes.
      </Alert>

      <Tabs
        allowScrollButtonsMobile
        scrollButtons="auto"
        value={activeSectionId}
        variant="scrollable"
        sx={{ mb: 3 }}
        onChange={(_event, value: DetailSectionId) => onSectionChange(value)}
      >
        {DETAIL_SECTIONS.map((section) => (
          <Tab key={section.id} label={section.label} value={section.id} />
        ))}
      </Tabs>

      {activeSectionId === 'custom-links' ? (
        <Box sx={{ pb: 4, pt: 4 }}>
          <CustomLinksPanel fields={fields} />
        </Box>
      ) : fields.length > 0 ? (
        <DetailsFieldsPanel activeSectionId={activeSectionId} fields={fields} />
      ) : (
        <Stack spacing={2}>
          <Alert severity="info">{activeSection?.label ?? 'This section'} has no fields in the current contract.</Alert>
        </Stack>
      )}
    </TabPanelShell>
  );
}
